import React, {useState} from 'react';
import {IconButton, InputAdornment, Popover, Typography} from '@mui/material';
import type {FullManifestation} from 'services/metadata/types';
import {useLocalization} from 'localization';
import {
  ActivityIndicator,
  Layout,
  RemoveIcon,
  SearchIcon,
  TextField,
} from 'components';
import {type SearchError, useGlobalSearch} from '../hooks/useGlobalSearch';

const originBottomLeft = {
  vertical: 'bottom',
  horizontal: 'left',
} as const;

export const IsbnSearch = () => {
  const {search, navigateToManifestation} = useGlobalSearch();
  const {t} = useLocalization();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = React.useState<
    SearchError | undefined
  >();
  const [anchorElSearch, setAnchorElSearch] = React.useState<
    (EventTarget & HTMLButtonElement) | null
  >(null);
  const searchIsOpen = Boolean(anchorElSearch);
  const anchorElSearchId = searchIsOpen ? 'simple-popover' : undefined;
  const handleOpenSearchMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElSearch(event.currentTarget);
  };
  const handleCloseSearchMenu = () => {
    setAnchorElSearch(null);
  };

  const onSearch = () => {
    setSearchLoading(true);

    const response = search(searchText);
    if (!response || response === 'notFound' || response === 'invalidLength') {
      setSearchError(response);
      setSearchLoading(false);
      return;
    }

    response.then(body => {
      if (!body || body === 'notFound' || body === 'invalidLength') {
        setSearchError(body || 'notFound');
        setSearchLoading(false);
        return;
      }

      setSearchLoading(false);
      handleCloseSearchMenu();
      navigateToManifestation(body as FullManifestation);
    });
  };

  return (
    <>
      <IconButton
        aria-describedby={anchorElSearchId}
        onClick={handleOpenSearchMenu}
        sx={({palette}) => ({
          p: 0.5,
          color: palette.secondary.light,
        })}>
        <SearchIcon />
      </IconButton>
      <Popover
        id={anchorElSearchId}
        open={searchIsOpen}
        anchorEl={anchorElSearch}
        onClose={() => handleCloseSearchMenu()}
        anchorOrigin={originBottomLeft}>
        <Layout padding={1}>
          <form
            onSubmit={e => {
              e.preventDefault();
              onSearch();
            }}>
            <Typography
              variant={'label'}
              color={searchError !== undefined ? 'red' : undefined}>
              {t(`error.search.${searchError}`)}
            </Typography>
            <TextField
              type={'text'}
              name={'search'}
              value={searchText}
              placeholder={'ISBN/EAN'}
              onChange={e => {
                setSearchError(undefined);
                setSearchText(
                  // Only enter numbers
                  e.target.value ? e.target.value.replace(/\D/g, '') : '',
                );
              }}
              layout={{
                width: '300px',
              }}
              autoFocus
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchText ? (
                        <IconButton
                          size="small"
                          onClick={() => setSearchText('')}>
                          <RemoveIcon />
                        </IconButton>
                      ) : null}
                      <IconButton onClick={() => onSearch()}>
                        {searchLoading ? (
                          <ActivityIndicator color="inherit" size={'medium'} />
                        ) : (
                          <SearchIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </form>
        </Layout>
      </Popover>
    </>
  );
};
